<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import lx from '../../utils/lx'
import { bindLazyVideo } from '../../utils/lazyVideo'
import VideoPlayHint from './VideoPlayHint.vue'
import { ASSETS, LX_BIDS } from './constants'

const props = defineProps<{
  isMobile: boolean
}>()

const mediaRef = ref<HTMLElement | null>(null)
const videoRef = ref<HTMLVideoElement | null>(null)
const isHovering = ref(false)
const showBar = ref(false)
const isInView = ref(true)
const mobileActivated = ref(false)
const mobileLoading = ref(false)
const mobileError = ref(false)
const mobileNeedsTap = ref(false)

let hideTimer: number | undefined
let visibilityObserver: IntersectionObserver | undefined
let mobilePreloadObserver: IntersectionObserver | undefined
let unbindLazy: (() => void) | undefined
let playToken = 0
let mobilePlayReported = false
let mobileErrorTimer: number | undefined
let isWeChatBrowser = false
let mobileRetryCount = 0

const clearHideTimer = () => {
  if (hideTimer !== undefined) {
    window.clearTimeout(hideTimer)
    hideTimer = undefined
  }
}

/** 鼠标不动约 2.5s 后隐藏控制条；再动则重新出现 */
const revealBar = () => {
  if (!isHovering.value) return
  showBar.value = true
  clearHideTimer()
  hideTimer = window.setTimeout(() => {
    if (isHovering.value) showBar.value = false
  }, 2500)
}

/** 确保已写入 src（进屏懒加载；hover 时兜底立刻加载） */
const ensureVideoSrc = () => {
  const video = videoRef.value
  if (!video || video.getAttribute('src') || video.currentSrc) return
  video.dataset.lazyLoaded = '1'
  video.src = ASSETS.ctaVideo
  video.load()
}

const waitCanPlay = (video: HTMLVideoElement): Promise<void> => {
  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      video.removeEventListener('canplay', done)
      video.removeEventListener('loadeddata', done)
      resolve()
    }
    video.addEventListener('canplay', done, { once: true })
    video.addEventListener('loadeddata', done, { once: true })
    video.load()
  })
}

const tryPlay = async (token: number) => {
  const video = videoRef.value
  if (!video || token !== playToken || !isHovering.value || !isInView.value) return

  ensureVideoSrc()
  await waitCanPlay(video)
  if (token !== playToken || !isHovering.value) return

  const reportIfStarted = (wasPaused: boolean) => {
    if (wasPaused && token === playToken) {
      lx.moduleClick(LX_BIDS.ctaMc, { index: 1 })
    }
  }

  try {
    const wasPaused = video.paused
    video.muted = false
    await video.play()
    reportIfStarted(wasPaused)
    return
  } catch {
    // 降级静音
  }

  if (token !== playToken || !isHovering.value) return
  try {
    const wasPaused = video.paused
    video.muted = true
    await video.play()
    reportIfStarted(wasPaused)
  } catch (error) {
    console.warn('[CtaBanner] 视频播放失败：', error)
  }
}

const onEnter = async () => {
  if (props.isMobile || !isInView.value) return
  isHovering.value = true
  revealBar()
  await nextTick()
  const token = ++playToken
  void tryPlay(token)
}

/** 移出视频区域：只收起控制条，不暂停；滚出屏幕才停 */
const onLeave = () => {
  if (props.isMobile) return
  isHovering.value = false
  showBar.value = false
  clearHideTimer()
}

const playMobileVideoWithSound = () => {
  const video = videoRef.value
  if (!props.isMobile || !mobileActivated.value || !video) return
  video.volume = 0.5
  video.muted = false
  video.defaultMuted = false
  video.removeAttribute('muted')
  try {
    void video.play().catch((error) => {
      if (!mobileActivated.value) return
      if (error instanceof DOMException && error.name === 'AbortError') return
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        mobileLoading.value = false
        mobileNeedsTap.value = true
      } else {
        mobileLoading.value = true
      }
      console.warn('[CtaBanner] 移动端有声自动播放失败：', error)
    })
  } catch (error) {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      mobileLoading.value = false
      mobileNeedsTap.value = true
    } else {
      mobileLoading.value = true
    }
    console.warn('[CtaBanner] 移动端有声自动播放失败：', error)
  }
}

const clearMobileErrorTimer = () => {
  if (mobileErrorTimer === undefined) return
  window.clearTimeout(mobileErrorTimer)
  mobileErrorTimer = undefined
}

const preloadMobileVideo = () => {
  const video = videoRef.value
  if (!props.isMobile || isWeChatBrowser || !video || mobileActivated.value) return
  if (video.dataset.ctaPreloaded === '1' && !video.error) return
  video.pause()
  video.autoplay = false
  video.preload = 'metadata'
  video.poster = ASSETS.ctaPoster
  video.dataset.ctaPreloaded = '1'
  video.src = ASSETS.ctaVideoMobile
  video.load()
}

const onMobilePlayTap = () => {
  if (!props.isMobile) return
  const video = videoRef.value
  if (!video) return

  mobileActivated.value = true
  mobileLoading.value = true
  mobileError.value = false
  mobileNeedsTap.value = false
  mobilePlayReported = false
  mobileRetryCount = 0
  clearMobileErrorTimer()

  video.poster = ASSETS.ctaPoster
  // 大文件保持 metadata 预加载；开始播放后由浏览器通过 HTTP Range 边播边缓冲。
  video.preload = 'metadata'
  video.autoplay = true
  video.muted = false
  video.defaultMuted = false
  video.removeAttribute('muted')
  const hasPreloadedSource = !isWeChatBrowser &&
    video.dataset.ctaPreloaded === '1' &&
    !video.error
  if (!hasPreloadedSource) {
    // 微信 WebView 可能保留点击前 load() 产生的旧 MediaError，首次点击时重置。
    video.pause()
    video.removeAttribute('src')
    video.load()
    video.dataset.ctaPreloaded = '1'
    video.src = ASSETS.ctaVideoMobile
    video.load()
  }
  playMobileVideoWithSound()
}

const onMobileLoading = () => {
  if (!props.isMobile || !mobileActivated.value) return
  mobileLoading.value = true
}

const onMobileCanPlay = () => {
  if (!props.isMobile || !mobileActivated.value) return
  clearMobileErrorTimer()
  mobileRetryCount = 0
  playMobileVideoWithSound()
}

const onMobilePlaying = () => {
  if (!props.isMobile || !mobileActivated.value) return
  clearMobileErrorTimer()
  mobileRetryCount = 0
  mobileLoading.value = false
  mobileError.value = false
  mobileNeedsTap.value = false
  if (!mobilePlayReported) {
    mobilePlayReported = true
    lx.moduleClick(LX_BIDS.ctaMc, { index: 1 })
  }
}

const onMobileError = () => {
  if (!props.isMobile || !mobileActivated.value) return
  mobileLoading.value = true
  mobileError.value = false
  mobileNeedsTap.value = false
  clearMobileErrorTimer()
  const retryDelay = Math.min(1200 * (2 ** mobileRetryCount), 5000)
  mobileRetryCount += 1
  mobileErrorTimer = window.setTimeout(() => {
    mobileErrorTimer = undefined
    const video = videoRef.value
    if (!props.isMobile || !mobileActivated.value || !video) return
    if (!video.error || video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      playMobileVideoWithSound()
      return
    }
    video.pause()
    video.removeAttribute('src')
    video.load()
    video.dataset.ctaPreloaded = '1'
    video.src = ASSETS.ctaVideoMobile
    video.load()
    playMobileVideoWithSound()
  }, retryDelay)
}

const retryMobilePlayback = () => {
  const video = videoRef.value
  if (!video || !mobileActivated.value) return
  mobileLoading.value = true
  mobileError.value = false
  mobileNeedsTap.value = false
  mobileRetryCount = 0
  clearMobileErrorTimer()
  video.removeAttribute('src')
  video.load()
  video.src = ASSETS.ctaVideoMobile
  video.load()
  playMobileVideoWithSound()
}

const resumeMobilePlayback = () => {
  mobileLoading.value = true
  mobileNeedsTap.value = false
  playMobileVideoWithSound()
}

const stopPlayback = () => {
  clearMobileErrorTimer()
  mobileRetryCount = 0
  playToken += 1
  isHovering.value = false
  showBar.value = false
  clearHideTimer()
  const video = videoRef.value
  if (!video) return
  video.pause()
  if (props.isMobile) {
    video.autoplay = false
    mobileActivated.value = false
    mobileLoading.value = false
    mobileError.value = false
    mobileNeedsTap.value = false
  }
}

onMounted(() => {
  isWeChatBrowser = /MicroMessenger/i.test(navigator.userAgent)
  if (!mediaRef.value) return
  const video = videoRef.value
  if (video) video.volume = 0.5
  const videoSrc = ASSETS.ctaVideo
  if (!props.isMobile) {
    unbindLazy = bindLazyVideo(video, videoSrc, {
      rootMargin: '320px 0px',
      autoplay: false,
      sequential: false,
      showFirstFrame: true
    })
  }
  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      const visible = Boolean(entry?.isIntersecting)
      isInView.value = visible
      if (!visible) stopPlayback()
    },
    { threshold: 0.15 }
  )
  visibilityObserver.observe(mediaRef.value)
  mobilePreloadObserver = new IntersectionObserver(
    ([entry]) => {
      if (entry?.isIntersecting) preloadMobileVideo()
    },
    { rootMargin: '480px 0px', threshold: 0 }
  )
  mobilePreloadObserver.observe(mediaRef.value)
})

watch(() => props.isMobile, (isMobile) => {
  if (isMobile) {
    stopPlayback()
    preloadMobileVideo()
  }
  else {
    mobileActivated.value = false
    mobileLoading.value = false
    mobileError.value = false
    mobileNeedsTap.value = false
  }
})

onBeforeUnmount(() => {
  playToken += 1
  clearHideTimer()
  visibilityObserver?.disconnect()
  mobilePreloadObserver?.disconnect()
  unbindLazy?.()
  clearMobileErrorTimer()
  stopPlayback()
})
</script>

<template>
  <section class="cta-banner-section" :data-mv-bid="LX_BIDS.ctaMv" data-mv-key="cta">
    <div class="cta-banner-heading">
      <div class="cta-title">
        <span class="line line-color">想在美团做直播？</span>
        <span class="line">缺主播、缺时间、缺场地设备</span>
      </div>
      <div class="cta-sub">看美团智播如何帮助本地生活商家轻松开播</div>
    </div>

    <div
      ref="mediaRef"
      class="cta-banner-media"
      :class="{ 'is-hovering': isHovering, 'show-bar': showBar }"
      @mouseenter="onEnter"
      @mouseleave="onLeave"
      @mousemove="revealBar"
    >
      <video
        ref="videoRef"
        class="cta-video"
        :poster="props.isMobile ? ASSETS.ctaPoster : undefined"
        playsinline
        :webkit-playsinline="props.isMobile ? '' : undefined"
        :x5-playsinline="props.isMobile ? '' : undefined"
        :preload="props.isMobile ? 'metadata' : 'none'"
        controlslist="nodownload noplaybackrate"
        :controls="props.isMobile
          ? mobileActivated && !mobileLoading && !mobileError && !mobileNeedsTap
          : isHovering"
        @mousemove="revealBar"
        @canplay="onMobileCanPlay"
        @loadstart="onMobileLoading"
        @waiting="onMobileLoading"
        @stalled="onMobileLoading"
        @playing="onMobilePlaying"
        @error="onMobileError"
      />
      <button
        v-if="props.isMobile && !mobileActivated"
        class="cta-play-trigger"
        type="button"
        aria-label="播放美团智播产品宣传视频"
        @click.stop="onMobilePlayTap"
      >
        <VideoPlayHint />
      </button>
      <div
        v-if="props.isMobile && mobileLoading"
        class="cta-video-status"
        aria-live="polite"
      >
        <mtd-loading
          class="video-modal-loading"
          size="large"
          message="视频加载中…"
        />
      </div>
      <div
        v-else-if="props.isMobile && mobileError"
        class="cta-video-status cta-video-error"
        role="alert"
      >
        <span>视频加载失败，请稍后重试</span>
        <button type="button" @click="retryMobilePlayback">重新加载</button>
      </div>
      <div
        v-else-if="props.isMobile && mobileNeedsTap"
        class="cta-video-status cta-video-resume"
      >
        <button type="button" @click="resumeMobilePlayback">点击播放</button>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.cta-banner-section {
  width: 1200px;
  max-width: calc(100% - 48px);
  margin: 200px auto 0;
}

.cta-banner-heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-bottom: 48px;
  text-align: center;
}

.cta-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #171717;
  font-size: 48px;
  font-weight: 700;
  line-height: normal;
}

.line-color {
  color: #8932ff;
}

.cta-sub {
  color: #666;
  font-size: 18px;
  font-weight: 300; /* L */
  line-height: normal;
}

.cta-banner-media {
  position: relative;
  width: 1066px;
  max-width: 100%;
  height: 527px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 48px;
  background: #f0f0f0;
}

.cta-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #e8e8e8;
}

.cta-play-trigger {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.cta-video-status {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(17, 17, 17, 0.56);
}

.video-modal-loading {
  color: #fff;
}

.cta-video-error {
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  text-align: center;
}

.cta-video-error button {
  padding: 8px 18px;
  border: 1px solid rgba(255, 255, 255, 0.68);
  border-radius: 18px;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  font: inherit;
}

.cta-video-resume {
  background: rgba(17, 17, 17, 0.36);
}

.cta-video-resume button {
  padding: 10px 22px;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 22px;
  color: #fff;
  background: rgba(0, 0, 0, 0.36);
  font: inherit;
}

/* hover 期间始终保留 controls，用透明度做「静止隐藏」避免反复挂载打断播放 */
.cta-banner-media.is-hovering:not(.show-bar) .cta-video {
  &::-webkit-media-controls-panel {
    opacity: 0;
    transition: opacity 0.25s ease;
  }
}

.cta-banner-media.show-bar .cta-video {
  &::-webkit-media-controls-panel {
    opacity: 1;
    transition: opacity 0.25s ease;
  }
}

@media (max-width: 960px) {
  .cta-banner-section {
    width: auto;
    max-width: none;
    margin: 64px 20px 0;
  }

  .cta-title {
    font-size: 27px;
  }

  .cta-banner-media {
    height: auto;
    aspect-ratio: 1066 / 527;
    border-radius: 24px;
    cursor: pointer;
  }
}
</style>
